import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const SubtleLowPolyBackground = () => {
  const mountRef = useRef(null);
  const frameId = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const targetMouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const currentMount = mountRef.current;
 
    // Normal mode colors
    const colors = {
      text: 0x000000,
      background: 0x1a1a18, // Pure white background
      primary: 0xe0e0e0,    // Light gray - more visible
      secondary: 0xb89a7f,  // Soft gray
      accent:  0x555997,     // Medium light gray
      wireframe: 0xb8b8b8   // Visible but soft wireframe color
    };
    
    // Dark mode colors
    // const colors = {
    //     text: 0xffffff,    // White text for readability
    //     background: 0x1e1e1e, // Dark background
    //     primary: 0x2c2c2c, // Dark gray
    //     secondary: 0xffffff, // Slightly lighter gray
    //     accent: 0xffffff,  // Accent stays vivid
    //     wireframe: 0xffffff // Subtle but visible wireframe
    // };

    
    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(colors.background);
    scene.fog = new THREE.Fog(colors.background, 40, 100); // Increased fog for more subtlety
    
    const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ 
      alpha: false, 
      antialias: true,
      precision: 'highp'
    });
    
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = false; // Disabled shadows for cleaner look
    currentMount.appendChild(renderer.domElement);

    // Create very subtle shapes
    const shapes = [];
    
    // Main geometric cluster
    const mainGroup = new THREE.Group();
    
    // Central icosahedron (visible but clean)
    const icosaGeometry = new THREE.IcosahedronGeometry(2, 0);
    const icosaMaterial = new THREE.MeshLambertMaterial({ 
      color: colors.primary,
      transparent: true,
      opacity: 0.4, // More visible
      flatShading: true
    });
    const icosahedron = new THREE.Mesh(icosaGeometry, icosaMaterial);
    icosahedron.position.set(0, 0, -12); // Brought closer
    mainGroup.add(icosahedron);
    shapes.push(icosahedron);

    // Clean floating shapes
    for (let i = 0; i < 3; i++) { // Middle ground - 3 shapes
      const dodecaGeometry = new THREE.DodecahedronGeometry(1, 0);
      const dodecaMaterial = new THREE.MeshLambertMaterial({ 
        color: i % 2 === 0 ? colors.secondary : colors.accent,
        transparent: true,
        opacity: 0.3, // Visible but not distracting
        flatShading: true
      });
      const dodeca = new THREE.Mesh(dodecaGeometry, dodecaMaterial);
      
      const angle = (i / 3) * Math.PI * 2;
      dodeca.position.set(
        Math.cos(angle) * 10,
        Math.sin(i * 0.7) * 2.5,
        Math.sin(angle) * 10 - 6
      );
      mainGroup.add(dodeca);
      shapes.push(dodeca);
    }

    // Clean wireframe elements
    for (let i = 0; i < 4; i++) {
      const wireGeometry = new THREE.TetrahedronGeometry(0.6, 0);
      const wireMaterial = new THREE.MeshBasicMaterial({ 
        color: colors.wireframe,
        wireframe: true,
        transparent: true,
        opacity: 0.25 // Visible but subtle
      });
      const wireframe = new THREE.Mesh(wireGeometry, wireMaterial);
      
      wireframe.position.set(
        (Math.random() - 0.5) * 30,
        (Math.random() - 0.5) * 18,
        (Math.random() - 0.5) * 30 - 5
      );
      wireframe.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );
      mainGroup.add(wireframe);
      shapes.push(wireframe);
    }

    // Clean subtle crystals
    for (let i = 0; i < 5; i++) {
      const crystalGeometry = new THREE.ConeGeometry(0.4, 1.5, 5, 1);
      const crystalMaterial = new THREE.MeshLambertMaterial({ 
        color: [colors.primary, colors.secondary, colors.accent][i % 3],
        transparent: true,
        opacity: 0.25, // Visible but not distracting
        flatShading: true
      });
      const crystal = new THREE.Mesh(crystalGeometry, crystalMaterial);
      
      crystal.position.set(
        (Math.random() - 0.5) * 35,
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 35 - 8
      );
      crystal.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );
      mainGroup.add(crystal);
      shapes.push(crystal);
    }

    scene.add(mainGroup);

    // Very gentle lighting setup
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.95); // Brighter ambient for flat look
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.3); // Much softer
    directionalLight.position.set(10, 10, 5);
    scene.add(directionalLight);

    // Remove particle system entirely for cleaner look

    // Position camera at good distance
    camera.position.set(0, 5, 28);
    camera.lookAt(0, 0, 0);

    // Gentler mouse interaction
    const handleMouseMove = (event) => {
      targetMouse.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      targetMouse.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    document.addEventListener('mousemove', handleMouseMove);

    // Much more subtle animation
    const animate = () => {
      frameId.current = requestAnimationFrame(animate);

      const time = Date.now() * 0.0002; // Even slower

      // Very gentle mouse following
      mouseRef.current.x += (targetMouse.current.x - mouseRef.current.x) * 0.01;
      mouseRef.current.y += (targetMouse.current.y - mouseRef.current.y) * 0.01;

      // Minimal camera movement
      camera.position.x += (mouseRef.current.x * 1 - camera.position.x) * 0.005;
      camera.position.y += (mouseRef.current.y * 0.5 - camera.position.y) * 0.005;
      camera.lookAt(
        mouseRef.current.x * 0.5,
        mouseRef.current.y * 0.5,
        0
      );

      // Very subtle shape animations
      shapes.forEach((shape, index) => {
        if (shape.geometry.type === 'IcosahedronGeometry') {
          shape.rotation.x += 0.001; // Slower rotation
          shape.rotation.y += 0.002;
          shape.position.y = Math.sin(time + index) * 0.2; // Less movement
        } else if (shape.geometry.type === 'DodecahedronGeometry') {
          shape.rotation.x += 0.0008;
          shape.rotation.z += 0.001;
          shape.position.y += Math.sin(time * 0.3 + index) * 0.005; // Minimal movement
        } else if (shape.material.wireframe) {
          shape.rotation.x += 0.0005;
          shape.rotation.y += 0.0008;
        } else {
          shape.rotation.y += 0.0015;
          shape.position.x += Math.sin(time + index * 0.5) * 0.01; // Very subtle
        }
      });

      // Minimal main group rotation
      mainGroup.rotation.y += mouseRef.current.x * 0.0003;
      mainGroup.rotation.x += mouseRef.current.y * 0.0001;

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (frameId.current) {
        cancelAnimationFrame(frameId.current);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      if (currentMount && renderer.domElement) {
        currentMount.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return (
    <div 
      ref={mountRef} 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1
      }}
    />
  );
};

export default SubtleLowPolyBackground;